import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const speakers = [
  {
    name: 'Dr. Elena Kepin',
    title: 'Robotics Professor',
    org: 'MIT',
    expertise: 'Autonomous Systems',
    photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
  },
  {
    name: 'Prof. Kevin Tanaka',
    title: 'AI Research Lead',
    org: 'Google',
    expertise: 'Machine Learning in Robotics',
    photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80',
  },
  {
    name: 'Dr. Priya Patel',
    title: 'Director, Robotics Innovation Lab',
    org: 'Stanford',
    expertise: 'Human-Robot Interaction',
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80',
  },
  {
    name: 'Dr. Markus Weber',
    title: 'CTO',
    org: 'RoboTech Inc.',
    expertise: 'Industrial Robotics',
    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
  },
];

const Speakers = () => {
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray('.speaker-card').forEach((card, i) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: 'power2.out',
            delay: i * 0.1,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="speakers"
      ref={sectionRef}
      className="section-padding bg-surface dark:bg-dark-surface relative overflow-hidden"
    >
      <div className="section-container">
        {/* Header — editorial */}
        <div className="mb-16 lg:mb-20">
          <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
            SPEAKERS / JUDGES
          </span>
          <h2 className="section-title mb-6">Distinguished experts.</h2>
          <p className="section-desc">
            Learn from and be judged by the leading experts in robotics and
            artificial intelligence.
          </p>
        </div>

        {/* Editorial grid — real photos */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {speakers.map((speaker) => (
            <div
              key={speaker.name}
              className="speaker-card border border-border dark:border-dark-border rounded-card overflow-hidden hover:border-border-strong dark:hover:border-dark-border-strong hover:-translate-y-0.5 transition-all duration-200"
            >
              {/* Photo */}
              <div className="relative h-48 overflow-hidden bg-surface-secondary dark:bg-dark-surface-secondary">
                <img
                  src={speaker.photo}
                  alt={speaker.name}
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              <div className="p-6">
                <h3 className="text-base font-semibold text-text-primary dark:text-dark-text-primary mb-1">
                  {speaker.name}
                </h3>
                <p className="text-sm text-accent dark:text-dark-accent font-medium mb-1">
                  {speaker.title}
                </p>
                <p className="text-sm text-text-muted dark:text-dark-text-muted mb-4">
                  {speaker.org}
                </p>

                <div className="pt-4 border-t border-border dark:border-dark-border">
                  <span className="tech-label text-xs">
                    {speaker.expertise}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Speakers;